/**
 * **계정 비밀번호 갱신의 표현 한 곳** — 「안 바꿈」은 빈칸이지 해시가 아니다.
 *
 * ## 무엇이 결함이었나
 *
 * 종전에는 `GET` 이 내려 준 bcrypt 해시를 폼에 담았다가(`result.accountPwConfirm = result.accountPw`)
 * 그대로 `PUT` 으로 되돌려 보내는 것이 「비밀번호를 안 바꾼다」의 표현이었다. 서버가
 * `!target.getAccountPw().equals(request.getAccountPw())` 로 재암호화 여부를 정했으므로 그 왕복은
 * 성립했지만, 그러려면 **응답이 해시를 실어야 한다** — 계정을 하나라도 열어 볼 수 있는 사용자에게
 * 그 계정의 bcrypt 해시가 함께 내려갔다(보안 원장 ⑥).
 *
 * 서버가 그 통로를 닫았다(S1 회차): 응답에 `accountPw` 가 없고, 여섯 `PUT` 은 본문의 `accountPw` 를
 * **비었으면 유지 · 있으면 새 비밀번호(8~60자)** 로 읽는다. 그러니 화면이 할 일은 둘이다 —
 * 비밀번호 칸을 **빈칸으로 열고**([blankPassword]), 빈칸이면 두 키를 본문에서 뺀다([passwordPatch]).
 *
 * ## 왜 한 곳인가
 *
 * 같은 판정이 계정·회원 폼 **여섯 곳**에 사본으로 있었다(`AccountListView`·`AccountInfoView`·
 * `components/account/EditAccount`·`UserListView`·`UserListForCustView`·`AppUserListView` 의
 * `if (accountPw != accountPwConfirm)`). 여섯이 같은 규칙을 각자 적으면 한 곳만 고쳐질 때 어떤 화면은
 * 해시를 되돌려 보내고 어떤 화면은 빈칸을 보낸다 — 서버는 그 둘을 다르게 읽으므로, 갈리는 순간
 * 「안 바꿈」이 「빈 비밀번호로 바꿈」이 될 수도 있는 자리다. 그래서 판정은 이 파일 하나다.
 *
 * 순수 — I/O·전역 상태를 읽지 않고 인자를 바꾸지 않는다(단위 테스트가 앱 번들 밖에서 그대로 읽는다).
 */

import { without } from './without.js'

/**
 * 비밀번호 축의 폼 키 — `accountPwConfirm` 은 **화면에만 있는 칸**이지만 이 앱의 저장은 폼 객체를
 * 통째로 보내므로 본문에도 실려 왔다. 빈칸일 때 함께 빼는 것이 브리프가 정한 계약이다.
 */
export const PASSWORD_FIELDS = ['accountPw', 'accountPwConfirm']

/** 화면 문구 한 곳 — 컴포넌트 안에 한글을 다시 적지 않는다(i18n 키가 한국어 문장 자체다) */
export const PASSWORD_TEXT = {
  /** 여섯 폼이 각자 적던 문장을 그대로 옮겼다 — 기존 번역 키를 잃지 않는다 */
  mismatch: '비밀번호가 일치 하지 않습니다.',
  /** 수정 화면의 빈칸이 「비밀번호를 지운다」로 읽히지 않게 하는 한 줄 */
  keepHint: '비워 두면 기존 비밀번호를 그대로 둡니다.',
}

/**
 * 빈칸 판정 — **서버와 같은 술어**다.
 *
 * 서버 `AccountPasswordPolicy.onUpdate` 의 첫 갈래가 `requested.isNullOrBlank()` 라, 공백만 든 값
 * (`'   '`)도 거기서 `Keep` 으로 접힌다. 화면이 그것을 「새 비밀번호」로 실어 보내면 서버는 아무것도
 * 바꾸지 않는데 화면은 「수정 성공」을 띄운다 — 사람이 비밀번호를 바꿨다고 믿는 자리가 된다.
 * 그래서 빈칸의 뜻을 서버와 맞춰 여기서도 함께 뺀다.
 *
 * **값 자체는 자르지 않는다.** 앞뒤에 공백이 붙은 진짜 비밀번호(`' pw '`)는 그대로 보낸다 — 서버도
 * 실린 값을 자르지 않는다(공백도 비밀번호의 일부다).
 */
const isBlank = (value) => typeof value !== 'string' || value.trim() === ''

/**
 * 비밀번호 칸의 초기값 — 조회 응답을 폼에 담을 때 **언제나** 이 값으로 덮는다.
 *
 * 서버가 이미 `accountPw` 를 내리지 않으므로 대부분은 없는 키를 덮는 셈이지만, 배포 순서(서버 →
 * 어드민)가 어긋난 환경이나 옛 응답을 그대로 담는 경로에서도 **해시가 폼에 들어오지 못하게** 한다.
 * 매번 새 객체를 돌려준다(공유 객체를 폼에 붙이면 한 화면의 입력이 다른 화면에 남는다).
 */
export function blankPassword() {
  return { accountPw: '', accountPwConfirm: '' }
}

/**
 * 폼 → 전송 본문의 비밀번호 축 판정.
 *
 * - 둘 다 비었다 → **안 바꾼다**. 두 키를 본문에서 빼서 서버가 `Keep` 으로 읽게 한다.
 * - 둘이 다르다 → 오류(문구와 어느 칸인지를 함께 돌려준다).
 * - 같고 값이 있다 → 새 비밀번호. 본문은 그대로다(길이 규칙은 서버가 판정한다 — 8~60자).
 *
 * 하나만 빈 경우는 **다르다**에 든다(빈칸 판정이 둘 다를 함께 보므로 위 첫 갈래에 걸리지 않는다).
 *
 * 생성(`POST`)에서 둘 다 비면 키가 빠지고 서버의 `@NotBlank`(`AccountPwValid`)가 「비밀번호는 필수
 * 입력 항목입니다」를 필드 오류로 돌려준다 — 종전에 빈 문자열을 보내던 때와 같은 답이다.
 *
 * @param {?object} form 폼 값 — **바꾸지 않는다**
 * @returns {{ok: true, changed: boolean, payload: object}|{ok: false, field: string, message: string}}
 */
export function passwordPatch(form) {
  const source = form || {}

  if (isBlank(source.accountPw) && isBlank(source.accountPwConfirm)) {
    return { ok: true, changed: false, payload: withoutPassword(source) }
  }

  if (source.accountPw !== source.accountPwConfirm) {
    return { ok: false, field: 'accountPwConfirm', message: PASSWORD_TEXT.mismatch }
  }

  return { ok: true, changed: true, payload: Object.assign({}, source) }
}

/** 비밀번호 두 키를 뺀 사본 — 「키를 빼고 보낸다」는 [without] 한 곳이 한다 */
function withoutPassword(source) {
  return without(source, PASSWORD_FIELDS)
}
